import React, {useEffect, useState} from 'react'
import {useCustomerStore, useGlobalStore} from '../../store/hooks/useStore'
import {getFormattedDate} from '../../utils/utils'
import {Box, Button, Center, Flex, Stack, Text} from '../../vanilla'
import {BaseButton} from '../../vanilla'
import {FeefoVerifiedIcon, IcelandVerifiedIcon} from '../icons'
import {Link} from '../link'
import {ReviewStars} from '../review-stars'
import {useCredentialModal} from '../../contexts'
import {CredentialModalPlacementMap} from '../../types/ui'
import { getDeviceType } from '../../utils/utils'
import { isMobile } from '../../utils/utils'

interface FeefoReviewsProps {
  productId?: string
  writeReviewUrl: string | null
  hasReviews: boolean
}

interface IReview {
  products: {
    rating: {
      rating: number
    }
    review: string
  }[]
  customer: {
    display_name: string
  } | null
  last_updated_date: string
}

const FEEFO_API_PAGE_SIZE = 6

const FeefoReviews = (props: FeefoReviewsProps) => {
  const {productId, writeReviewUrl, hasReviews} = props
  const [reviews, setReviews] = useState<IReview[]>([])
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const {isRegistered} = useCustomerStore()
  const credentialModal = useCredentialModal()
  const [feefoMerchantIdentifier, setFeefoMerchantIdentifier] = useState<string>('')
  const [reviewSummary, setReviewSummary] = useState({
    pages: 0,
    count: 0,
  })
  const {getCustomSitePreferenceById, isCustomSitePreferenceSet, customSitePreferences} =
    useGlobalStore()

  const isMobileDevice = isMobile()

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('FeefoSupplierMerchantIdentifier')) {
        await getCustomSitePreferenceById('FeefoSupplierMerchantIdentifier')
      }
      setFeefoMerchantIdentifier(customSitePreferences['FeefoSupplierMerchantIdentifier'] as string)
    })()
  })

  useEffect(() => {
    const fetchReviews = async () => {
      await loadReviews()
    }
    if (hasReviews) {
      fetchReviews().catch(console.error)
    }
  }, [productId, isRegistered, hasReviews, feefoMerchantIdentifier])

  const loadReviews = async () => {
    setIsLoading(true)
    if (feefoMerchantIdentifier) {
      const result = await fetch(
        `https://api.feefo.com/api/10/importedreviews/product?merchant_identifier=${feefoMerchantIdentifier}&product_sku=${productId}&full_thread=include&since_period=all&unanswered_feedback=exclude&page_size=${FEEFO_API_PAGE_SIZE}&page=${page}&unanswered_feedback=exclude`,
      ).then((response) => response.json())
    
    if (result) {
      setReviews((prevReviews) => [...prevReviews, ...result.reviews])
      setReviewSummary({
        pages: result.summary.meta.pages,
        count: result.summary.meta.count,
      })
      setPage((prevPage) => prevPage + 1)
    }
    setIsLoading(false)
  }
  }

  return (
    <Box width="full" bg="white">
      <Stack>
        <Stack
          direction={['column', 'row', 'row']}
          padding="20px"
          gap="16px"
          justifyContent="space-between"
        >
          <Flex>
            <Stack>
              <Text variant="heading0">Customer Reviews</Text>
              <Text
                variant="text3"
                color="textMuted"
              >{`(${reviewSummary?.count} customer reviews)`}</Text>
            </Stack>
          </Flex>
          <Flex>
            {isRegistered ? (
              <BaseButton
                variant="secondary"
                width="auto"
                size="sm"
                {...(writeReviewUrl ? {as: Link, to: writeReviewUrl} : {})}
              >
                <Text variant="text4">Write a review</Text>
              </BaseButton>
            ) : (
              <Button
                variant="outlineDark"
                width="auto"
                size="sm"
                onClick={() => {
                  const modalPlacement = isMobileDevice ? CredentialModalPlacementMap.BOTTOM : CredentialModalPlacementMap.ATTACHED
                  credentialModal.setModalPlacementType(modalPlacement)
                  credentialModal.onToggle()
                }}
              >
                Sign in to leave a review
              </Button>
            )}
          </Flex>
        </Stack>
        {reviews?.map((reviewObject, idx) => {
          const {products, customer, last_updated_date} = reviewObject
          const displayName = customer && customer?.display_name
          const reviewDetail = products[0]
          const {rating, review} = reviewDetail
          return (
            <Stack key={idx} gap="12px" borderTop="1px" padding="20px" borderColor="gray100">
              <Stack gap="2px">
                <Stack direction="row" justifyContent="space-between">
                  <Stack gap="0px">
                    {/* <Flex>{calculateStarRating(rating.rating).map((star) => star)}</Flex> */}
                    <ReviewStars rating={rating.rating} />
                    <Text variant="text1" color="textMuted">{`Submitted by ${
                      displayName || 'Iceland Customer'
                    } on ${getFormattedDate(last_updated_date)}`}</Text>
                  </Stack>
                  <Flex>
                    {idx % 2 === 0 ? (
                      <FeefoVerifiedIcon boxSize="44px" />
                    ) : (
                      <IcelandVerifiedIcon boxSize="44px" />
                    )}
                  </Flex>
                </Stack>
              </Stack>
              <Flex>
                <Text variant="text3">{review}</Text>
              </Flex>
            </Stack>
          )
        })}
        {isLoading &&
          Array(FEEFO_API_PAGE_SIZE)
            .fill(0)
            .map((_, idx) => <FeefoReviewSkeleton key={'feefo-review-skeleton-' + idx} />)}

        <Box padding="20px" borderTop="1px" borderColor="gray100">
          <Center>
            {reviewSummary.count > 0 && (
              <Button
                variant="outlineDark"
                data-test-selector="show-more-reviews-button"
                onClick={() => loadReviews()}
                disabled={page > reviewSummary.pages || isLoading}
              >
                Show More Reviews
              </Button>
            )}
          </Center>
        </Box>
      </Stack>
    </Box>
  )
}

const FeefoReviewSkeleton = () => {
  return (
    <Stack gap="16px" borderTop="1px" padding="20px" borderColor="gray100">
      <Stack gap="2px">
        <Stack direction="row" justifyContent="space-between">
          <Stack gap="4px">
            <Box height="20px" width="100px" bg="gray100" />
            <Box bg="gray100" style={{width: 280, height: 18}} />
          </Stack>
          <Flex>
            <Box width="48px" height="28px" bg="gray100" />
          </Flex>
        </Stack>
      </Stack>
      <Flex>
        <Box width="full" height="24px" bg="gray100" style={{maxWidth: 550}} />
      </Flex>
    </Stack>
  )
}

export default FeefoReviews
